import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppConfigProvider } from '@app/app.config.provider';
import { IImportSettings } from '@models/import/import-settings';
import { ImporterState } from '@models/import/importer-state';
import { IPollFileImport } from '@models/import/poll-file-import';
import { IImportPollRequest, IImportPollResponse } from '@models/import/start-import';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class ImportService {
  public dialogLoadedFrom: 'sim-button' | 'import-button';

  ///////////
  // URLS //
  /////////
  private importSettingsURL: string;
  private pollFileImportURL: string;
  private pollAggregationImportURL: string;
  private initiateSQLImportURL: string;
  private initiateImport: string;
  private initiateImportList: string;
  private importLocalURL: string;

  //////////////////
  // Dialog Sizes //
  //////////////////

  private readonly isHalfSized$ = new BehaviorSubject<boolean>(false);

  get isHalfSized() {
    return this.isHalfSized$.value;
  }

  set isHalfSized(val) {
    this.isHalfSized$.next(val);
  }

  get isHalfSizedListener() {
    return this.isHalfSized$.asObservable();
  }

  private readonly isMaximized$ = new BehaviorSubject<boolean>(false);

  get isMaximized() {
    return this.isMaximized$.value;
  }

  set isMaximized(val) {
    this.isMaximized$.next(val);
  }

  get isMaximizedListener() {
    return this.isMaximized$.asObservable();
  }

  private readonly importerStatus$ = new BehaviorSubject<ImporterState>(ImporterState.idle);

  get importStatus() {
    return this.importerStatus$.value;
  }

  set importStatus(importStatus: ImporterState) {
    this.importerStatus$.next(importStatus);
  }

  get importStatusListener$() {
    return this.importerStatus$.asObservable();
  }

  constructor(private readonly appConfig: AppConfigProvider, private readonly http: HttpClient) {
    this.appConfig.environment.subscribe(env => {
      this.importSettingsURL = `${env.API.REFERENCE_DATA}/api/import/import_settings`;
      this.pollFileImportURL = `${env.API.REFERENCE_DATA}/api/import/PollFileImport`;
      this.initiateImport = `${env.API.REFERENCE_DATA}/api/import/`;
      this.initiateImportList = `${env.API.REFERENCE_DATA}/api/import/list`;
      this.pollAggregationImportURL = `${env.API.REFERENCE_DATA}/api/import/PollAggregationImport`;
      this.initiateSQLImportURL = `${env.API.REFERENCE_DATA}/api/import/sql`;
      this.importLocalURL = `${env.API.REFERENCE_DATA}/api/import/ImportLocal`;
    });
  }

  public getImportSettings(): Promise<IImportSettings> {
    return this.http
      .get<IImportSettings>(this.importSettingsURL)
      .pipe(
        map(res => {
          // 🛠 Fixes the date to stop it returning Irish Standard Time
          if (res) {
            if (res.LastCompletedDepositImport) {
              res.LastCompletedDepositImport.import_start_date += 'z';
            }

            if (res.LastCompletedRateImport) {
              res.LastCompletedRateImport.import_start_date += 'z';
            }
          }
          return res;
        })
      )
      .toPromise();
  }

  public getLastImportDate(): Observable<Date> {
    return this.http.get<IImportSettings>(this.importSettingsURL).pipe(
      map(res => {
        if (res && res.LastCompletedDepositImport) {
          return new Date(res.LastCompletedDepositImport.import_start_date);
        }
        return null;
      })
    );
  }

  public initiateFileImport(importRequest: IImportPollRequest): Promise<any> {
    return this.http.post<IImportPollResponse>(this.initiateImport, importRequest).toPromise();
  }

  public initiateFileImportList(importRequest: IImportPollRequest[]): Promise<any> {
    return this.http.post<IImportPollResponse>(this.initiateImportList, importRequest).toPromise();
  }

  public initiateSqlImport(dataBaseName: string): Promise<IImportPollResponse> {
    if (dataBaseName) {
      return this.http.post<IImportPollResponse>(this.initiateSQLImportURL, {}).toPromise();
    }
  }

  public initiateLocalImport(file, filename) {
    const fileUploadForm: FormData = new FormData();
    fileUploadForm.append('uploadedDataset', file, filename);
    return this.http.post(this.importLocalURL, fileUploadForm);
  }

  public pollFileImport(importID: number): Promise<IImportPollResponse> {
    const request: IImportPollRequest = {
      FileName: null,
      Headers: null,
      ImportSource: null,
      ImportID: importID,
      IsMarketRate: false,
      IsExcel: false,
      selectedSheets: null
    };

    return this.http.post<IPollFileImport>(this.pollFileImportURL, request).toPromise();
  }

  public pollAggregationImport(importId: number): Promise<IImportPollResponse> {
    const request: IImportPollRequest = {
      FileName: null,
      Headers: null,
      ImportSource: null,
      ImportID: importId,
      IsMarketRate: false,
      IsExcel: false,
      selectedSheets: null
    };

    return this.http.post<IImportPollResponse>(this.pollAggregationImportURL, request).toPromise();
  }
}
